import * as React from "react"
import { Flex, Text } from "@chakra-ui/react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import * as Util from "../../../util"
import { Icon } from "../../common/icons"
import { RiChatPrivateFill } from "react-icons/ri"
import { ChatCloseIcon } from "./_chatCloseIcon"
import { ChatReplyIcon } from "./_chatReplyIcon"
import { ChatUserName } from "./_chatUserName"
import { ChatMessageText } from "./_chatMessageText"
import { ChatHoverButtons } from "./_chatHoverButtons"
import { MdLockClock } from "react-icons/md"
import { CgBlock } from "react-icons/cg"
import { Element } from "react-scroll"
import { ReplyModel } from "../../../recoil/atoms/chats/types"
import { useReactions } from "../../../hooks/useReactions"
import { MessageModel } from "../../../recoil/atoms/chats/types"
import { SDKContext } from "../../../context/SDKContext"
import {
  reactionsStatusAtom,
  reactionsStatusSelector,
  selectedReplyAtom
} from "../../../recoil/atoms/chats"
import { chatIdsSelector } from "../../../recoil/atoms/chats"
import { ChatStyles } from "./index"
import { UserAvatar } from "../../common/avatar"
import { UserType } from "../../../constants/enums"
import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import { useTranslation } from "react-i18next"
import ChatAttachment from "./_chatAttachment"
import useConfig from "src/hooks/useConfig"

const ChatType = window.MeetOnline.ChatType
const ChatStatus = window.MeetOnline.ChatStatus

interface ChatReplyMessageProps {
  reply: ReplyModel
  immediateParentChatId: string
  borderColor: string
  isApproved: boolean
  isPrivateMessage: boolean
  isModerator: boolean
  isMobile?: boolean
  isTransparent?: boolean
  isShowReplyIcon: boolean
  handleReply: (id: string) => void
  scrollToDownReplyMobile: (id: string, isReply?: boolean) => void
  toggleShowAttachment?: (link: Partial<ReplyModel>) => void
  positionModal: any
  handleAssignPosition: any
  handleChatMessageDelete: (id: string) => void
  toggleOvelayReply: any
  chat: Partial<MessageModel>
  scrollDown: () => void
  setSessionStorage: any
  isAudience: boolean
  localUserName: string
  localUserId: string
  styleschat: ChatStyles
  chatApprovalRequired: boolean
  canSendChat: boolean
  layerHeight: number
}

const _ChatReplyMessage: React.FC<ChatReplyMessageProps> = ({
  borderColor,
  isApproved,
  isPrivateMessage,
  isModerator,
  isMobile,
  isTransparent,
  isShowReplyIcon,
  handleReply,
  scrollToDownReplyMobile,
  handleAssignPosition,
  positionModal,
  handleChatMessageDelete,
  toggleOvelayReply,
  reply,
  immediateParentChatId,
  chat,
  scrollDown,
  setSessionStorage,
  isAudience,
  localUserName,
  localUserId,
  styleschat,
  chatApprovalRequired,
  canSendChat,
  layerHeight,
  toggleShowAttachment
}) => {
  const { t } = useTranslation()
  const replyRef = React.useRef<HTMLDivElement>(null)
  const reactionsRef = React.useRef<HTMLDivElement>(null)
  const setChats = useSetRecoilState(chatIdsSelector)
  const [hovered, setHovered] = React.useState<boolean>(false)
  const [isReply, setIsReply] = React.useState(false)
  const { isTablet } = useRecoilValue(viewDependingLayoutAtom)
  const [selectedReplyId, setSelectedReplyId] =
    useRecoilState(selectedReplyAtom)
  const isLocalUserChatAuthor = React.useMemo(() => {
    return localUserId === reply.author.id ? true : false
  }, [localUserId, reply.author.id])
  const isAuthorModerator = React.useMemo(() => {
    return reply?.author.type === UserType.MODERATOR
  }, [reply?.author.type])

  const isCloseIcon = React.useMemo<boolean>(() => {
    if (isMobile) {
      return false
    }
    return (
      (isModerator && hovered) ||
      (!isModerator && hovered && isLocalUserChatAuthor)
    )
  }, [hovered, isLocalUserChatAuthor, isMobile, isModerator])

  const isChatReply = React.useMemo(() => {
    if (isMobile) {
      return isReply ? false : true
    }
    return true
  }, [isMobile, isReply])
  const meetingClient = React.useContext(SDKContext)
  const { disableReplies } = useConfig()

  const setReactionsStatus = useSetRecoilState(reactionsStatusSelector)
  const reactionsStatusList = useRecoilValue(reactionsStatusAtom)

  const localEmodji = React.useMemo(() => {
    const array = reactionsStatusList.filter(item => item[0] === reply.id)
    return array.length > 0 ? array[0][1] : []
  }, [reactionsStatusList, reply.id])

  // toggle the reply flag after unselected or other chat message is selected
  React.useEffect(() => {
    if (!selectedReplyId || selectedReplyId?.replyId !== reply?.id)
      setIsReply(false)
  }, [selectedReplyId])

  const removeReaction = (emodji: string) => {
    meetingClient?.removeReaction(
      reply?.id,
      emodji,
      reply.parentChatId,
      immediateParentChatId
    )

    if (localEmodji.length > 0) {
      const reactions = localEmodji.filter(item => {
        return item !== emodji
      })
      const data = {
        [`${reply?.id}`]: reactions
      }

      setReactionsStatus({
        type: "DELETE",
        payload: { id: reply?.id, reaction: emodji }
      })

      setSessionStorage(data, "rewrite")
    }
  }

  const handleReaction = React.useCallback(
    (emodji: string): void => {
      meetingClient?.sendReaction(
        reply.id,
        emodji,
        reply.parentChatId,
        immediateParentChatId
      )
      if (localEmodji.length > 0) {
        const reactions = [emodji].concat(localEmodji)
        const data = {
          [`${reply.id}`]: reactions
        }
        setSessionStorage(data, "add")
        setReactionsStatus({
          type: "UPDATE",
          payload: { id: reply.id, reactions: reactions }
        })
      } else {
        const reactions = [emodji]
        const data = {
          [`${reply.id}`]: reactions
        }

        setSessionStorage(data, "add")
        setReactionsStatus({ type: "ADD", payload: [reply.id, reactions] })
      }
      if (isMobile) {
        setIsReply(false)
      }
    },
    [
      meetingClient,
      reply.id,
      reply.parentChatId,
      immediateParentChatId,
      localEmodji,
      isMobile,
      setSessionStorage,
      setReactionsStatus
    ]
  )

  const { ReactionsTrigger, ReactionItem, ReactionsModal, isOpenReplyModal } =
    useReactions(handleReaction, Util.emodjiList, scrollDown)

  const marginTopReply = React.useMemo(() => {
    if (isMobile && !isTransparent) {
      return "0rem"
    }
    /* if (isMobile && isTransparent) {
      return "0.25rem"
    } */
    return "unset"
  }, [isMobile, isTransparent])

  const isReactionsTrigger = React.useMemo(() => {
    if (isMobile) {
      return !isLocalUserChatAuthor && isReply ? true : false
    }
    return !isLocalUserChatAuthor ? true : false
  }, [isLocalUserChatAuthor, isMobile, isReply])

  const heightHoverButton = React.useMemo(() => {
    const replyContainer = document.getElementById(`reply-${reply.id}`)
    if (
      reply.reactions !== undefined &&
      replyContainer?.offsetHeight <= 90 &&
      reactionsRef?.current?.offsetHeight < 30
    ) {
      return "60%"
    }
    if (
      reply.reactions !== undefined &&
      replyContainer?.offsetHeight <= 90 &&
      reactionsRef?.current?.offsetHeight > 30
    ) {
      return "50%"
    }
    if (
      reply.reactions !== undefined &&
      replyContainer?.offsetHeight <= 120 &&
      reactionsRef?.current?.offsetHeight > 30
    ) {
      return "45%"
    }
    if (
      reply.reactions !== undefined &&
      replyContainer?.offsetHeight >= 120 &&
      reactionsRef?.current?.offsetHeight < 30
    ) {
      return "65%"
    }
    if (
      reply.reactions !== undefined &&
      replyContainer?.offsetHeight >= 120 &&
      reactionsRef?.current?.offsetHeight > 30
    ) {
      return "50%"
    }
    return "100%"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reply.reactions, reply.id, hovered])

  const handleDeleteBadReply = () => {
    setChats({
      type: "REMOVE",
      payload: { id: reply.id }
    })
  }

  const isPrivateReply = React.useMemo(() => {
    return reply.chatType === ChatType.PRIVATE ? true : false
  }, [reply.chatType])

  const isApprovedReply = React.useMemo(() => {
    return reply?.chatStatus === ChatStatus.APPROVED ||
      reply?.chatStatus === ChatStatus.AUTO_APPROVED
      ? true
      : false
  }, [reply?.chatStatus])

  const avatarStyles = React.useMemo(() => {
    return { width: "1.5rem", height: "1.5rem" }
  }, [])

  // show hover button when mouse is hover or chat is published
  const showHoverButton = hovered || reply.published

  // remove the bottom radius if reaction modal is opened
  const bottomRadius = isMobile && isOpenReplyModal && !isTransparent && "0px"

  return (
    <Element name={`reply-${reply.id}`}>
      <Flex
        id={`reply-${reply.id}`}
        border={`2px ${
          reply.badReply ? "dotted" : isApprovedReply ? "solid" : "dotted"
        } ${borderColor}`}
        borderWidth={isTransparent ? "0px" : "2px"}
        marginTop={marginTopReply}
        width={isMobile ? "fit-content" : "100%"}
        minWidth={
          isMobile && !canSendChat && isReply && isShowReplyIcon
            ? "250px"
            : isMobile && (isOpenReplyModal ? "290px" : "70px")
        }
        borderRadius="0.6rem"
        pos="relative"
        /*  boxShadow={isMobile && !isTransparent && "md"} */
        flexDirection="column"
        ref={replyRef}
        onMouseEnter={!isMobile ? () => setHovered(true) : null}
        onMouseLeave={!isMobile ? () => setHovered(false) : null}
        zIndex={layerHeight}
      >
        <Flex
          id={`notification-${reply?.id}`}
          display="none"
          className="rainbow"
        ></Flex>
        {!isMobile && (
          <ReactionsModal styleschat={styleschat} isMobile={isMobile} />
        )}
        {isCloseIcon && (
          <ChatCloseIcon
            styleschat={styleschat}
            id={reply.id}
            isMobile={isMobile}
            callback={
              reply.badReply ? handleDeleteBadReply : handleChatMessageDelete
            }
          />
        )}
        <Flex
          id={`reply-bg-container-${reply.id}`}
          bgColor={
            isTransparent
              ? "rgba(0, 0, 0, 0.5)"
              : styleschat.servicePanelElementBackground
              ? styleschat?.servicePanelElementBackground
              : "white"
          }
          w="100%"
          ref={replyRef}
          pos="relative"
          display="flex"
          width="100%"
          borderRadius="0.47rem"
          borderBottomLeftRadius={bottomRadius}
          borderBottomRightRadius={bottomRadius}
          sx={{ p: "0.3rem" }}
          onClick={
            isMobile && isShowReplyIcon && !isTransparent
              ? () => {
                  setIsReply(prev => !prev)
                  scrollToDownReplyMobile(reply.id, true)
                  // toggle the reply message selection
                  setSelectedReplyId(
                    isReply
                      ? null
                      : { id: reply.parentChatId, replyId: reply.id }
                  )
                }
              : null
          }
        >
          <ChatHoverButtons
            styleschat={styleschat}
            widthButton={"90px"}
            positionX={"-1.3%"}
            id={reply.id}
            parentReply={reply.parentChatId}
            hasAttachment={!!reply.attachmentURL}
            immediateParentChatId={immediateParentChatId}
            published={reply.published}
            toggleChat={() => {
              !!reply.attachmentURL
                ? toggleShowAttachment(reply)
                : toggleOvelayReply(reply, replyRef)
            }}
            hovered={showHoverButton && !reply.badReply}
            heightHoverButton={heightHoverButton}
            alignButtons={reply.reactions !== undefined ? "end" : "center"}
            isModerator={isModerator}
            isApproved={isApprovedReply}
            // isLocalUserChatAuthor={isLocalUserChatAuthor}
            hideUpproveButton={isPrivateMessage}
            chatApprovalRequired={chatApprovalRequired}
            isAuthorModerator={isAuthorModerator}
          />
          <Flex flexDirection="column">
            <Flex marginLeft="0.1rem" marginTop="0.1rem">
              <UserAvatar
                avatar={reply.author?.avatar}
                avatarStyle={avatarStyles}
                name={reply.author?.name}
                size="xs"
                src={reply.author?.avatar}
                colorDisk={reply?.author?.color}
              />
            </Flex>
            <Flex flexDirection="row">
              <Flex
                alignItems="end"
                height={"100%"}
                textColor={"gray.500"}
                zIndex={10000000}
                pl="0.35rem"
                pt="0.3rem"
                width="1.3rem"
                minWidth="21px"
              >
                {isPrivateMessage && !isTransparent && !reply.badReply && (
                  <Icon
                    src={RiChatPrivateFill}
                    tooltip="Private"
                    textColor={
                      styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                    }
                  />
                )}
                {!isMobile && reply.badReply && !isTransparent && (
                  <Icon
                    src={CgBlock}
                    tooltip={t("Message blocked")}
                    color={
                      styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                    }
                  />
                )}

                {!isMobile &&
                  !isModerator &&
                  !isApprovedReply &&
                  !reply.badReply &&
                  !isTransparent && (
                    <Icon
                      src={MdLockClock}
                      textColor={
                        styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                      }
                      tooltip={t("Pending approval")}
                      marginRight="0.2rem"
                    />
                  )}
              </Flex>
            </Flex>
          </Flex>

          <Flex
            flexDirection="column"
            width="100%"
            marginLeft="0.33rem"
            marginRight={isMobile && "0.33rem"}
          >
            <ChatUserName
              isMobile={isMobile}
              isModerator={isModerator}
              isTransparent={isTransparent}
              author={reply.author}
              styleschat={styleschat}
              hovered={showHoverButton && isModerator && !reply.badReply}
              message={reply.message}
              targetUser={reply.targetUser}
              isPrivateMessage={isPrivateReply}
              localUserName={localUserName}
              localUserId={localUserId}
            />
            {reply.attachmentThumbnailURL && (
              <ChatAttachment chat={reply} hasMessage={!!reply?.message} />
            )}
            {/* {!isTransparent && ( */}
            <ChatMessageText
              isMobile={isMobile}
              styleschat={styleschat}
              hovered={showHoverButton && isModerator && !reply.badReply}
              message={reply.message}
              isApproved={isApproved}
              isTransparent={isTransparent}
              isReplyText={true}
            />
            {/* )} */}
            <Flex
              marginLeft={"0rem"}
              marginTop={!isTransparent && "0.1rem"}
              flexWrap="wrap"
              ref={reactionsRef}
              zIndex={100000}
            >
              {reply.reactions !== undefined && (
                <>
                  {Object.entries(
                    Util.removeUnnecessaryReactions(reply?.reactions)
                  ).map(reaction => {
                    return (
                      <ReactionItem
                        emodji={Util.convertToUnicode(
                          reaction[0],
                          Util.emodjiList
                        )}
                        key={`reaction-reply-${chat?.id}-${reaction[0]}`}
                        uniqKey={`reaction-reply-${chat?.id}-${reaction[0]}`}
                        total={reaction[1]}
                        isAddedReaction={localEmodji.includes(reaction[0])}
                        handleAddReaction={e => {
                          e.stopPropagation()
                          handleReaction(reaction[0])
                        }}
                        handleRemoveReaction={e => {
                          e.stopPropagation()
                          removeReaction(reaction[0])
                        }}
                        styleschat={styleschat}
                        isMobile={isMobile}
                      />
                    )
                  })}
                </>
              )}
            </Flex>
          </Flex>

          <Flex>
            {!isTransparent && (
              <Flex
                wordBreak="break-word"
                textColor={
                  styleschat?.commentTextColor
                    ? styleschat.commentTextColor
                    : "inherit"
                }
                fontFamily={
                  styleschat?.commentFontFamily
                    ? styleschat.commentFontFamily
                    : "inherit"
                }
                flexDirection="column"
              >
                <Flex marginLeft="auto" whiteSpace="nowrap">
                  <Text
                    fontSize="0.75rem"
                    color={styleschat?.servicePanelElementLabels ?? "#718096"}
                  >
                    {Util.formatTime24h(reply.timestamp)}
                  </Text>
                </Flex>
                <Flex marginTop="auto">
                  {isMobile &&
                    !isModerator &&
                    !isApprovedReply &&
                    !reply.badReply &&
                    !isTransparent && (
                      <Icon
                        src={MdLockClock}
                        textColor={
                          styleschat?.servicePanelElementPrimaryText ??
                          "#4A5568"
                        }
                        tooltip={t("Pending approval")}
                        marginRight="0.2rem"
                        containerStyles={{ marginLeft: "auto" }}
                      />
                    )}
                  {isReactionsTrigger && canSendChat && (
                    <Flex
                      marginLeft="auto"
                      alignItems="end"
                      fontSize={isMobile && "1.15em"}
                      marginRight={
                        (isModerator ||
                          (!isModerator && isLocalUserChatAuthor)) &&
                        isMobile &&
                        isReply &&
                        isShowReplyIcon &&
                        !isTransparent &&
                        "0.35rem"
                      }
                      height={"100%"}
                      zIndex={!isMobile && 10000000}
                    >
                      <ReactionsTrigger styleschat={styleschat} />
                    </Flex>
                  )}
                  {isMobile && reply.badReply && !isTransparent && (
                    <Icon
                      src={CgBlock}
                      tooltip={t("Message blocked")}
                      color={
                        styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                      }
                      containerStyles={{ marginLeft: "auto" }}
                    />
                  )}
                  {disableReplies === false && isChatReply && (
                    <ChatReplyIcon
                      styleschat={styleschat}
                      id={reply.id}
                      isMobile={isMobile}
                      isShowReplyIcon={isShowReplyIcon}
                      handleReply={
                        isMobile && isShowReplyIcon
                          ? () => {
                              setIsReply(prev => !prev)
                              setSelectedReplyId({
                                id: reply.parentChatId,
                                replyId: reply.id
                              })
                              scrollToDownReplyMobile(reply.id, true)
                            }
                          : handleReply
                      }
                      isReply={true}
                    />
                  )}
                  {(isModerator || (!isModerator && isLocalUserChatAuthor)) &&
                    isMobile &&
                    isReply &&
                    isShowReplyIcon &&
                    !isTransparent && (
                      <ChatCloseIcon
                        styleschat={styleschat}
                        id={reply.id}
                        isMobile={isMobile}
                        callback={
                          reply.badReply
                            ? handleDeleteBadReply
                            : handleChatMessageDelete
                        }
                      />
                    )}
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
        {isMobile && isReply && isShowReplyIcon && !isTransparent && (
          <Flex
            flexDir="column"
            bgColor={
              isTransparent
                ? "rgba(0, 0, 0, 0.5)"
                : styleschat.servicePanelElementBackground
                ? styleschat?.servicePanelElementBackground
                : "white"
            }
            borderBottomRightRadius={
              isMobile && isReply && !isTransparent && "0.5rem"
            }
            borderBottomLeftRadius={
              isMobile && isReply && !isTransparent && "0.5rem"
            }
            paddingBottom={isOpenReplyModal && "0.2rem"}
          >
            <ReactionsModal
              styleschat={styleschat}
              isMobile={isMobile}
              customStyles={{
                position: "relative",
                bottom: "unset",
                left: "unset",
                width: "calc(100% - 0.4rem)",
                marginLeft: "0.2rem"
              }}
            />
            {/* <ChatReplyInput
              hideEmodji={isMobile || isTablet}
              styleschat={styleschat}
              localUserId={localUserId}
              haveRiplys={chat?.replyChats && chat?.replyChats.length > 0}
              chatId={chat?.id}
              isLocalUserChatAuthor={isLocalUserChatAuthor}
              borderColor={borderColor}
              published={chat?.published}
              isApproved={isApproved}
              setIsReply={setIsReply}
              handleSendMessage={handleSendMessage}
              isPrivateMessage={isPrivateMessage}
              positionModal={positionModal}
              targetUser={reply.targetUser}
              handleAssignPosition={handleAssignPosition}
              isReply={isReply}
              isModerator={isModerator}
              isMobile={isMobile}
            /> */}
          </Flex>
        )}
      </Flex>
    </Element>
  )
}

export const ChatReplyMessage = React.memo(_ChatReplyMessage)
